/**
 * useServiceOrderDestination - Coordenadas do local da OS e último registro de
 * chegada do prestador (cartão de chegada no Resumo da OS). Validação definitiva na RPC.
 *
 * RELEASE: 2026-10-13 v1.1.0 - Gravação unificada em create/update_service_order
 *          (useSetServiceOrderDestination removido); queryKey com tenant e staleTime 0,
 *          alinhando aos demais hooks de OS e evitando cache vazio ao reabrir.
 * RELEASE: 2026-10-13 v1.0.0 - Versão inicial.
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

// Preserva o `this` do cliente (RPC fora da tipagem gerada)
const rpc = (name: string, args: Record<string, unknown>) =>
  (supabase.rpc as unknown as (n: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: { message: string } | null }>)
    .call(supabase, name, args);

export interface ServiceOrderArrival {
  at: string;
  data: { latitude?: number; longitude?: number; accuracy?: number; distance_m?: number | null; within_radius?: boolean | null;
    radius_m?: number; has_reference?: boolean; justification?: string | null; has_photo?: boolean };
}
export interface ServiceOrderDestination { lat: number | null; lng: number | null; arrival: ServiceOrderArrival | null }

export function useServiceOrderDestination(orderId: string | null | undefined) {
  const { currentTenant } = useAuth();
  return useQuery({
    queryKey: ['service-order-destination', currentTenant?.id, orderId],
    enabled: !!orderId && !!currentTenant?.id,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await rpc('get_service_order_destination', { p_id: orderId, p_tenant_hint: currentTenant?.id ?? null });
      if (error) throw new Error(error.message);
      return data as ServiceOrderDestination;
    },
  });
}
